import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DollarSign, Users, MessageSquare, Briefcase, Video, ArrowRight, Settings } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/lib/customSupabaseClient';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    messages: 0,
    clients: 0,
    projects: 0,
    videos: 0,
    revenue: 0,
    services: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkAdminAccess();
  }, [user]);

  const checkAdminAccess = async () => {
    const { data } = await supabase
      .from('admin_users')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (!data) {
      navigate('/login');
    } else {
      fetchStats();
    }
  };

  const fetchStats = async () => {
    const [messagesRes, clientsRes, portfolioRes, invoicesRes, videosRes, servicesRes] = await Promise.all([
      supabase.from('messages').select('id', { count: 'exact' }),
      supabase.from('clients').select('id', { count: 'exact' }),
      supabase.from('portfolio').select('id', { count: 'exact' }),
      supabase.from('invoices').select('total').eq('status', 'paid'),
      supabase.from('video_testimonials').select('id', { count: 'exact' }),
      supabase.from('services').select('id', { count: 'exact' })
    ]);

    const revenue = invoicesRes.data?.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0) || 0;

    setStats({
      messages: messagesRes.count || 0,
      clients: clientsRes.count || 0,
      projects: portfolioRes.count || 0,
      videos: videosRes.count || 0,
      services: servicesRes.count || 0,
      revenue: revenue
    });
    setLoading(false);
  };

  const statCards = [
    {
      title: 'Messages Reçus',
      value: stats.messages,
      icon: MessageSquare,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      link: '/messages'
    },
    {
      title: 'Services',
      value: stats.services,
      icon: Settings,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      link: '/services'
    },
    // {
    //   title: 'Clients Actifs',
    //   value: stats.clients,
    //   icon: Users,
    //   color: 'from-purple-500 to-purple-600',
    //   bgColor: 'bg-purple-50',
    //   textColor: 'text-purple-600',
    //   link: '/clients'
    // },
    {
      title: 'Projets Réalisés',
      value: stats.projects,
      icon: Briefcase,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      link: '/portfolio'
    },
    {
      title: 'Témoignages Vidéo',
      value: stats.videos,
      icon: Video,
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-rose-50',
      textColor: 'text-rose-600',
      link: '/testimonials'
    }
    // {
    //   title: 'Revenus Encaissés',
    //   value: `${stats.revenue.toLocaleString('fr-FR')} MAD`,
    //   icon: DollarSign,
    //   color: 'from-emerald-500 to-emerald-600',
    //   bgColor: 'bg-emerald-50',
    //   textColor: 'text-emerald-600',
    //   link: '/invoices'
    // }
  ];

  return (
    <AdminLayout>
      <Helmet>
        <title>Tableau de bord - Admin - Bigadsy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1">Vue d'ensemble de votre agence marketing et indicateurs clés</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3"></div>
            <p className="text-sm">Chargement des indicateurs...</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {statCards.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className="bg-white rounded-2xl p-6 border border-slate-200/90 shadow-xs hover:shadow-md transition-all flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className={`h-11 w-11 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center shadow-xs shadow-black/5`}>
                      <stat.icon className="h-5 w-5 text-white" />
                    </div>
                    <Link
                      to={stat.link}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50"
                      title="Consulter la rubrique"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                  <div className="text-2xl font-black text-slate-900 mb-1">{stat.value}</div>
                  <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider">{stat.title}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;